import React, { useEffect, useState } from 'react';
import useAolSaveHandler from '../../../app/useAolSaveHandler';
import { useAolStore } from '../../../context/AolContext';
import { useVesselStore } from '../../../context/VesselContext';
import { NoonReportInertGasProductionSectionFactory } from '../../../factory/ValidatedNoonReportFactory';
import { NoonReportSectionEnum } from '../../../types/ViewModel/AolEnums';
import { NoonReportSectionData } from '../../../types/ViewModel/NoonReportSectionData';
import { createSectionData, getConsumptionCost, getNumberValue, hasInvalidFields, hasMissingValues } from '../../../utils/aolFieldsHelper';
import { ErrorText } from '../../shared/error-text/ErrorText.style';
import { SectionInnerWrapper, SubSectionHeader } from '../aol-container/AolContainer.style';
import OdfjellInputComponent, { InputChangeData, TimeInputChangeData } from '../../common/odfjell-input-component/OdfjellInputComponent';
import useReadOnlyUtils from 'src/utils/useReadOnlyUtils';
import { Consumption } from 'src/types/ViewModel/Aol/Consumption';
import ConsumptionContainer from '../consumption/ConsumptionContainer';
import { EngineDataTypeEnum } from 'src/types/BusinessModel/AolEnums';
import useAolItemsHelper from 'src/utils/aol/useAolItemsHelper';

type NitrogenGeneratorVM = {
    generatorId: number;
    runningHours?: number;
};

type InertGasProductionVM = {
    inertGasGeneratorConsumptionMgo?: number;
    nitrogenGenerators?: NitrogenGeneratorVM[];
    sectionData?: NoonReportSectionData;
    notValidFieldsArray?: string[];
    hasInvalidFields?: boolean;
    isInitialized?: boolean;
    consumption?: Consumption[];
};

const initialVM: InertGasProductionVM = {
    nitrogenGenerators: [],
};

const InertGasProductionSectionComponent: React.FC = () => {
    const { selectedAolGroup, selectedAolDataItem, aolSyncEvent } = useAolStore();
    const { getVoyageFuelPrice } = useAolItemsHelper();
    const [vm, setVM] = useState<InertGasProductionVM>(initialVM);
    const { vessel } = useVesselStore();
    const { saveAolEvent, saveSection } = useAolSaveHandler();
    const [hasEmptyValues, setHasEmptyValues] = useState<boolean>(false);
    const { checkIsAolReadOnly } = useReadOnlyUtils();

    useEffect(() => {
        if (
            saveAolEvent &&
            saveAolEvent.section === NoonReportSectionEnum.InertGasProduction &&
            selectedAolDataItem &&
            saveAolEvent.aolDataItem.id === selectedAolDataItem.id
        ) {
            if (saveAolEvent.type === 'save_section') save();
            if (saveAolEvent.type === 'save_section_done') initVM();
        }
    }, [saveAolEvent]);

    useEffect(() => {
        if (!selectedAolDataItem) return;
        if (!vm.isInitialized) {
            initVM();
        }
    }, [selectedAolDataItem]);

    useEffect(() => {
        if (aolSyncEvent && aolSyncEvent.noonReport && aolSyncEvent.type === 'sync_hours') {
            initVM();
        }
    }, [aolSyncEvent]);

    const isReadOnly = checkIsAolReadOnly(vm?.sectionData);

    const initVM = () => {
        if (!selectedAolDataItem) return;
        const noonReport = selectedAolDataItem.noonReport;

        let nitrogenGenerators: NitrogenGeneratorVM[] = [];
        if (noonReport && noonReport.inertGasProductionSection) {
            nitrogenGenerators = noonReport.inertGasProductionSection.nitrogenGenerators.map((n) => {
                return { generatorId: n.generatorId, runningHours: n.runningHours };
            });
        } else {
            for (let i = 0; i < 5; i++) {
                nitrogenGenerators.push({ generatorId: i + 1, runningHours: 0 });
            }
        }

        const vm: InertGasProductionVM = {
            inertGasGeneratorConsumptionMgo: getNumberValue(noonReport?.inertGasProductionSection?.inertGasGeneratorConsumptionMgo, 2, true),
            nitrogenGenerators: nitrogenGenerators.sort((a, b) => a.generatorId - b.generatorId),
            sectionData: noonReport?.inertGasProductionSection?.sectionData ?? createSectionData(NoonReportSectionEnum.InertGasProduction),
            notValidFieldsArray: [],
            hasInvalidFields: false,
            isInitialized: true,
            consumption: selectedAolDataItem?.noonReport?.inertGasProductionSection?.consumption ?? [],
        };

        setHasEmptyValues(false);
        setVM({ ...vm });
    };

    const onInputChange = (changeObject: InputChangeData) => {
        const name = changeObject.event.target.name;
        const value = changeObject.event.target.value;
        const voyageFuelPrice = changeObject.extraArgs?.aolItem?.voyageNumber ? getVoyageFuelPrice(changeObject.extraArgs) : null;
        changeObject.dataObject.cost = getConsumptionCost(changeObject.dataObject.fuelType, voyageFuelPrice, +value);

        changeObject.dataObject[name] = value ? +value : undefined;

        hasInvalidFields(vm, name, changeObject.isValid);
        setVM({ ...vm });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const onTimeInputChange = (changeObject: TimeInputChangeData) => {
        changeObject.dataObject[changeObject.name] = changeObject.hours;
        hasInvalidFields(vm, changeObject.name, changeObject.isValid);
        setVM({ ...vm });
        if (hasEmptyValues) setHasEmptyValues(false);
    };

    const addConsumptionRow = (consumption: Consumption[]) => {
        consumption.forEach((c) => {
            vm.consumption.push(c);
        });

        setVM({ ...vm });
    };

    const save = () => {
        const ignoreList = [''];
        if (!vessel?.vesselConfigurationV3?.isEquippedWithInertGasGenerator) {
            ignoreList.push('inertGasGeneratorConsumptionMgo');
        }
        let hasMissingValue = hasMissingValues(vm, ignoreList);

        if (!hasMissingValue && vm.nitrogenGenerators) {
            hasMissingValue = vm.nitrogenGenerators.some((p) => hasMissingValues(p));
        }
        if (hasMissingValue) {
            setHasEmptyValues(true);
        } else {
            const data = new NoonReportInertGasProductionSectionFactory().createBM(vm);
            if (!data.sectionData || data.sectionData.section === 0)
                data.sectionData = {
                    noonReportId: 0,
                    section: NoonReportSectionEnum.InertGasProduction,
                    isApproved: false,
                    isCompleted: false,
                    role: 1,
                };
            if (hasEmptyValues) setHasEmptyValues(false);

            if (!vm.hasInvalidFields) {
                saveSection(data);
            }
        }
    };

    return (
        <>
            {vm && (
                <>
                    {vessel?.vesselConfigurationV3?.isEquippedWithInertGasGenerator && (
                        <SectionInnerWrapper>
                            <SubSectionHeader>Inert gas generator</SubSectionHeader>
                            {selectedAolDataItem.aolVersion >= 5 ? (
                                <ConsumptionContainer
                                    aolItem={selectedAolDataItem}
                                    engineType={EngineDataTypeEnum.InertGasGenerator}
                                    consumption={vm?.consumption}
                                    isReadOnly={isReadOnly}
                                    onChange={onInputChange}
                                    addConsumptionRow={addConsumptionRow}
                                />
                            ) : (
                                <OdfjellInputComponent
                                    type={'number'}
                                    isReadOnly={isReadOnly}
                                    description={'MGO consumption'}
                                    value={vm.inertGasGeneratorConsumptionMgo}
                                    dataObject={vm}
                                    name={'inertGasGeneratorConsumptionMgo'}
                                    onChange={onInputChange}
                                    unit={'MT'}
                                    min={0}
                                    max={50}
                                />
                            )}
                        </SectionInnerWrapper>
                    )}
                    <SectionInnerWrapper>
                        <SubSectionHeader>Nitrogen production</SubSectionHeader>
                        {vm.nitrogenGenerators &&
                            vm.nitrogenGenerators.map((nitrogenGenerator, i) => (
                                <OdfjellInputComponent
                                    key={`nitrogenGenerator.runningHours_${i}`}
                                    type={'time'}
                                    isReadOnly={isReadOnly}
                                    description={`Compr. ${i + 1}`}
                                    max={selectedAolGroup?.aolHours}
                                    dataObject={nitrogenGenerator}
                                    name={'runningHours'}
                                    value={nitrogenGenerator.runningHours}
                                    onTimeInputChange={onTimeInputChange}
                                />
                            ))}
                    </SectionInnerWrapper>
                </>
            )}
            {hasEmptyValues && <ErrorText>You must complete the section before saving.</ErrorText>}
        </>
    );
};

export default InertGasProductionSectionComponent;
